import { Controller, useFormContext } from 'react-hook-form';
import { CustomInput, DatePicker, Input, UISelect } from '../common/forms';
import { Translate, useTranslate } from '~/i18n';
import cn from 'classnames';
import { DateTime } from 'luxon';
import InputMask from 'react-input-mask';
import React from 'react';

export const ArrivalAndDepartureDetails = ({
   airCarrierList,
   seaCarriersList,
   landCarriersList,
   airPortsList,
   seaPortsList,
   landPortsList,
}: any) => {
   const { translate } = useTranslate();
   const {
      register,
      control,
      formState: { errors },
      watch,
      getValues,
      setValue,
   } = useFormContext<any>();
   const entryTypeValue = watch('entryType');
   const isEntryTypeSelected = Boolean(entryTypeValue);
   const carrier =
      entryTypeValue === 'LAND'
         ? landCarriersList
         : entryTypeValue === 'SEA'
         ? seaCarriersList
         : airCarrierList;
   const port =
      entryTypeValue === 'LAND'
         ? landPortsList
         : entryTypeValue === 'SEA'
         ? seaPortsList
         : airPortsList;

   const PurposeOptions = [
      {
         value: 'UMRAH',
         label: translate({ id: 'visasManager:umrah' }),
      },
      {
         value: 'TOURISM',
         label: translate({ id: 'visasManager:tourism' }),
      },
   ];

   const validateFlightPrice = (value: any) => {
      if (value <= 0) {
         return translate({ id: 'validation:required' });
      }

      if (value?.startsWith?.('0')) {
         return translate({ id: 'validation:not starting with zero' });
      }

      const validNumberRegex = /^[0-9٠-٩]+(\.[0-9٠-٩]{1,2})?$/;

      if (!validNumberRegex.test(value)) {
         return translate({ id: 'validation:invalid format hotel price' });
      }

      return true;
   };

   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:arrival and departure details" />
            </div>

            <div className="w-full">
               <div className="mb-5">
                  <Controller
                     name="visitPurpose"
                     control={control}
                     rules={{ required: translate({ id: 'validation:required' }) }}
                     render={({ field: { onChange, value } }) => (
                        <UISelect
                           placeholder={translate({
                              id: 'visasManager:purpose',
                           })}
                           label={translate({
                              id: 'visasManager:select purpose',
                           })}
                           id="visitPurpose"
                           value={PurposeOptions.find(opt => opt.value === value)}
                           options={PurposeOptions}
                           error={errors.visitPurpose?.message}
                           onChange={({ value }: any) => onChange(value)}
                           isRequired
                        />
                     )}
                  />
               </div>
               <div className="mb-5">
                  <Input
                     id="flightPrice"
                     label={translate({
                        id: 'visasManager:price',
                     })}
                     placeholder={translate({
                        id: 'visasManager:enter price',
                     })}
                     isRequired
                     error={errors.flightPrice?.message}
                     {...register(`flightPrice`, {
                        validate: validateFlightPrice,
                     })}
                  />
               </div>
               <div
                  className={
                     cn(
                        'grid w-full grid-cols-1 gap-5 mb-5 ',
                        entryTypeValue == 'AIR' || entryTypeValue == 'SEA'
                     ) && 'grid w-full grid-cols-1 gap-5 mb-5 lg:grid-cols-2'
                  }>
                  {entryTypeValue == 'AIR' || entryTypeValue == 'SEA' ? (
                     <Controller
                        control={control}
                        name="entryFlightNo"
                        rules={{
                           validate:
                              entryTypeValue === 'AIR' || entryTypeValue === 'SEA'
                                 ? {
                                      validate: value =>
                                         value > 0 || translate({ id: 'validation:required' }),
                                   }
                                 : {},
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="99999999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="entryFlightNo"
                                    label={translate({
                                       id: 'visasManager:entry flight number',
                                    })}
                                    placeholder={translate({
                                       id: 'visasManager:number',
                                    })}
                                    isRequired={entryTypeValue == 'AIR' || entryTypeValue == 'SEA'}
                                    error={errors.entryFlightNo?.message}
                                    ref={ref}
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                  ) : null}
                  <Controller
                     name="entryPort"
                     control={control}
                     rules={{ required: translate({ id: 'validation:required' }) }}
                     render={({ field: { onChange, value } }) => (
                        <UISelect
                           placeholder={translate({
                              id: 'visasManager:select port',
                           })}
                           label={translate({
                              id: 'visasManager:entry port',
                           })}
                           id="entryPort"
                           value={port?.find((opt: any) => opt.value === value) || value}
                           options={port}
                           error={errors.entryPort?.message}
                           onChange={({ value }: any) => onChange(value)}
                           isRequired
                           isDisabled={!isEntryTypeSelected}
                        />
                     )}
                  />
               </div>
               <div className="grid w-full grid-cols-1 gap-5 mb-5 lg:grid-cols-2">
                  <Controller
                     control={control}
                     name="dateArrivalFlight"
                     rules={{ required: translate({ id: 'validation:required' }) }}
                     render={({ field: { onChange, value } }) => (
                        <DatePicker
                           placeholderText={translate({
                              id: 'common:date',
                           })}
                           id="dateArrivalFlight"
                           minDate={new Date(new Date().getTime() + 24 * 60 * 60 * 1000)}
                           onChange={val => {
                              const d = DateTime.fromJSDate(val as Date);
                              onChange(d.toFormat('yyyy-MM-dd'));
                              setValue('flightDepartureDate', '');
                           }}
                           selected={
                              getValues('dateArrivalFlight') &&
                              DateTime.fromFormat(getValues('dateArrivalFlight'), 'yyyy-MM-dd')
                                 ?.isValid
                                 ? DateTime.fromFormat(
                                      getValues('dateArrivalFlight'),
                                      'yyyy-MM-dd'
                                   ).toJSDate()
                                 : null
                           }
                           label={translate({ id: 'visasManager:arrival date' })}
                           error={errors.dateArrivalFlight?.message}
                           value={value?.split?.('T')?.[0] || value}
                           isRequired
                        />
                     )}
                  />
                  <Input
                     id="arrivalFlightTime"
                     label={<Translate id="visasManager:arrival time" />}
                     placeholder={translate({ id: 'visasManager:select arrival time' })}
                     isRequired
                     error={errors.arrivalFlightTime?.message}
                     {...register('arrivalFlightTime', {
                        required: translate({ id: 'validation:required' }),
                     })}
                     type="time"
                  />
               </div>
               <div className="mb-5">
                  <Controller
                     name="carrierIn"
                     control={control}
                     rules={{ required: translate({ id: 'validation:required' }) }}
                     render={({ field: { onChange, value } }) => (
                        <UISelect
                           placeholder={translate({
                              id: 'visasManager:select arrival carrier',
                           })}
                           label={translate({
                              id: 'visasManager:arrival carrier',
                           })}
                           id="carrierIn"
                           value={carrier?.find((opt: any) => opt.value === value) || value}
                           options={carrier}
                           error={errors.carrierIn?.message}
                           onChange={({ value }: any) => onChange(value)}
                           isRequired
                           isDisabled={!isEntryTypeSelected}
                        />
                     )}
                  />
               </div>
               <div className="grid w-full grid-cols-1 gap-5 mb-5 lg:grid-cols-2">
                  <Controller
                     control={control}
                     name="flightDepartureDate"
                     rules={{
                        required: translate({ id: 'validation:required' }),
                     }}
                     render={({ field: { onChange, value } }) => (
                        <DatePicker
                           placeholderText={translate({
                              id: 'common:date',
                           })}
                           id="flightDepartureDate"
                           minDate={
                              getValues('dateArrivalFlight')
                                 ? DateTime.fromFormat(
                                      getValues('dateArrivalFlight'),
                                      'yyyy-MM-dd'
                                   ).toJSDate() ?? new Date()
                                 : new Date()
                           }
                           label={translate({ id: 'visasManager:departure date' })}
                           error={errors.flightDepartureDate?.message}
                           value={value?.split?.('T')?.[0] || value}
                           selected={
                              getValues('flightDepartureDate') &&
                              DateTime.fromFormat(getValues('flightDepartureDate'), 'yyyy-MM-dd')
                                 ?.isValid
                                 ? DateTime.fromFormat(
                                      getValues('flightDepartureDate'),
                                      'yyyy-MM-dd'
                                   ).toJSDate()
                                 : null
                           }
                           onChange={val => {
                              const d = DateTime.fromJSDate(val as Date);
                              onChange(d.toFormat('yyyy-MM-dd'));
                           }}
                           isRequired
                        />
                     )}
                  />
                  <Input
                     id="flightDepartureTime"
                     label={<Translate id="visasManager:departure time" />}
                     placeholder={translate({ id: 'visasManager:select departure time' })}
                     isRequired
                     error={errors.flightDepartureTime?.message}
                     {...register('flightDepartureTime', {
                        required: translate({ id: 'validation:required' }),
                     })}
                     type="time"
                  />
               </div>
               <div className="grid w-full grid-cols-1 gap-5 mb-5 lg:grid-cols-2">
                  <Controller
                     name="carrierOut"
                     control={control}
                     rules={{ required: translate({ id: 'validation:required' }) }}
                     render={({ field: { onChange, value } }) => (
                        <UISelect
                           placeholder={translate({
                              id: 'visasManager:select departure carrier',
                           })}
                           label={translate({
                              id: 'visasManager:departure carrier',
                           })}
                           id="carrierOut"
                           value={carrier?.find((opt: any) => opt.value === value) || value}
                           options={carrier}
                           error={errors.carrierOut?.message}
                           onChange={({ value }: any) => onChange(value)}
                           isRequired
                           isDisabled={!isEntryTypeSelected}
                        />
                     )}
                  />
                  <Controller
                     name="exitPort"
                     control={control}
                     rules={{ required: translate({ id: 'validation:required' }) }}
                     render={({ field: { onChange, value } }) => (
                        <UISelect
                           placeholder={translate({
                              id: 'visasManager:select port',
                           })}
                           label={translate({
                              id: 'visasManager:exit port',
                           })}
                           id="exitPort"
                           value={port?.find((opt: any) => opt.value === value) || value}
                           options={port}
                           error={errors.exitPort?.message}
                           onChange={({ value }: any) => onChange(value)}
                           isRequired
                           isDisabled={!isEntryTypeSelected}
                        />
                     )}
                  />
               </div>
               <div className="mb-5">
                  {entryTypeValue == 'AIR' || entryTypeValue == 'SEA' ? (
                     <Controller
                        control={control}
                        name="departureFlightNo"
                        rules={{
                           validate:
                              entryTypeValue === 'AIR' || entryTypeValue === 'SEA'
                                 ? {
                                      validate: value =>
                                         value > 0 || translate({ id: 'validation:required' }),
                                   }
                                 : {},
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="99999999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="departureFlightNo"
                                    label={translate({
                                       id: 'visasManager:departure flight number',
                                    })}
                                    placeholder={translate({
                                       id: 'visasManager:enter departure flight number',
                                    })}
                                    isRequired
                                    error={errors.departureFlightNo?.message}
                                    ref={ref}
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                  ) : null}
               </div>
            </div>
         </div>
      </>
   );
};
